import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationButton } from '@shared/components/confirmation-dialog/confirmation-dialog.component';

export interface ConfirmationData {
  header: string | Promise<string>;
  message: string | Promise<string>;
  type?: 'btnCancel' | 'custom';
  buttons?: ConfirmationButton[];
}

@Injectable({ providedIn: 'root' })
export class ConfirmationDialogService {
  private readonly translateService = inject(TranslateService);

  isVisible = signal(false);
  title = signal('');
  message = signal('');
  buttons = signal<ConfirmationButton[]>([]);
  hasCustomContent = signal(false);

  private confirmCallback?: () => void;
  private rejectCallback?: () => void;

  setConfirmationData(
    confirmationData: ConfirmationData,
    onConfirm: () => void,
    onReject: () => void
  ): void {
    this.confirmCallback = onConfirm;
    this.rejectCallback = onReject;

    // Handle async translations
    Promise.all([
      this.resolveValue(confirmationData.header),
      this.resolveValue(confirmationData.message),
    ]).then(([resolvedHeader, resolvedMessage]) => {
      this.title.set(resolvedHeader);
      this.message.set(resolvedMessage);

      // Setup buttons based on type
      if (confirmationData.type === 'btnCancel') {
        this.buttons.set([
          {
            label: this.translateService.instant('common.actions.cancel'),
            action: 'cancel',
            outlined: true,
          },
          {
            label: this.translateService.instant('common.actions.confirm'),
            action: 'confirm',
            severity: 'danger',
          },
        ]);
      } else if (confirmationData.buttons) {
        this.buttons.set(confirmationData.buttons);
      }

      this.isVisible.set(true);
    });
  }

  private resolveValue(value: string | Promise<string>): Promise<string> {
    if (value instanceof Promise) {
      return value;
    }
    return Promise.resolve(this.translateService.instant(value));
  }

  onDialogAction(action: string): void {
    if (action === 'confirm') {
      this.confirmCallback?.();
    } else {
      this.rejectCallback?.();
    }
    this.isVisible.set(false);
  }

  onDialogHide(): void {
    this.rejectCallback?.();
    this.isVisible.set(false);
  }

  closeDialog(): void {
    this.isVisible.set(false);
  }
}
